import { apiMutate, getApiErrorMessage, isApiError, parseApiErrorBody } from "~/utils/apiClient";

const { VITE_CATALOG_API } = import.meta.env;

// Mirrors the backend ModifyDatasetTablesRequest/Response contract for
// POST /api/catalog/v1/datasets/{id}/tables.
type DatasetTableAssociationType = "owned" | "referenced";

export interface DatasetTableAssociation {
  table_fqn: string;
  association_type?: DatasetTableAssociationType;
}

export interface DatasetTableRef {
  table_fqn: string;
}

export interface ModifyDatasetTablesRequest {
  add?: DatasetTableAssociation[];
  remove?: DatasetTableRef[];
}

export interface ModifiedDatasetTable {
  table_fqn: string;
  association_type: DatasetTableAssociationType;
}

export interface SkippedDatasetTable {
  table_fqn: string;
  reason: string;
  status_code: number;
}

export interface ModifyDatasetTablesResponse {
  errors: boolean;
  added: ModifiedDatasetTable[];
  removed: ModifiedDatasetTable[];
  skipped_additions: SkippedDatasetTable[];
  skipped_removals: SkippedDatasetTable[];
}

/**
 * Generic transport for the bulk table association endpoint. Sends a delta
 * ({ add?, remove? }) and returns the outcome. Throws ApiError on a non-2xx
 * response (e.g. when every requested change failed).
 */
export const modifyDatasetTables = (
  datasetId: string,
  request: ModifyDatasetTablesRequest
): Promise<ModifyDatasetTablesResponse> =>
  apiMutate<ModifyDatasetTablesResponse>(
    `${VITE_CATALOG_API}/v1/datasets/${datasetId}/tables`,
    "POST",
    request
  );

// When every requested change fails, the endpoint raises with
// `detail = { message, skipped_additions: [...], skipped_removals: [...] }` rather than
// the standard `{ detail: string }` envelope, so pull the per-table reasons out of it.
// Returns the distinct reasons joined into one message, or null if none are present.
const extractSkippedReasons = (
  body: Record<string, unknown> | null,
  field: "skipped_additions" | "skipped_removals"
): string | null => {
  const detail = body?.detail;
  if (!detail || typeof detail !== "object") {
    return null;
  }
  const skips = (detail as Record<string, unknown>)[field];
  if (!Array.isArray(skips)) {
    return null;
  }
  const reasons = skips
    .map((skip) => (skip as SkippedDatasetTable)?.reason)
    .filter((reason): reason is string => typeof reason === "string");
  const distinct = [...new Set(reasons)];
  return distinct.length ? distinct.join(" ") : null;
};

/**
 * Disassociate a single table from a dataset. Resolves on success, including the
 * idempotent case where the table was not associated to begin with. Throws an Error
 * carrying the API's human-readable reason when the removal fails.
 */
export const removeDatasetTable = async (datasetId: string, tableFqn: string): Promise<void> => {
  let response: ModifyDatasetTablesResponse;
  try {
    response = await modifyDatasetTables(datasetId, {
      remove: [{ table_fqn: tableFqn }],
    });
  } catch (e) {
    if (isApiError(e)) {
      throw new Error(
        extractSkippedReasons(parseApiErrorBody(e), "skipped_removals") ?? getApiErrorMessage(e)
      );
    }
    throw e;
  }

  // A 2xx can still report a failed removal when a caller batches multiple tables (a
  // single-table removal never hits this: a total failure is thrown above). Surface it.
  const failure = response.skipped_removals.find((skip) => skip.status_code >= 400);
  if (failure) {
    throw new Error(failure.reason);
  }
};

export interface AddDatasetTablesResult {
  /** Tables newly associated with the dataset (empty when every selection was a no-op). */
  added: ModifiedDatasetTable[];
  /** Additions that failed (status_code >= 400); idempotent no-ops are excluded. */
  failed: SkippedDatasetTable[];
}

/**
 * Associate one or more tables with a dataset. Resolves with the tables that were added
 * and any that failed, so the caller can report partial success. Throws an Error carrying
 * the API's human-readable reason(s) when the whole request fails (nothing was added).
 */
export const addDatasetTables = async (
  datasetId: string,
  tableFqns: string[]
): Promise<AddDatasetTablesResult> => {
  let response: ModifyDatasetTablesResponse;
  try {
    response = await modifyDatasetTables(datasetId, {
      add: tableFqns.map((table_fqn) => ({ table_fqn })),
    });
  } catch (e) {
    if (isApiError(e)) {
      throw new Error(
        extractSkippedReasons(parseApiErrorBody(e), "skipped_additions") ?? getApiErrorMessage(e)
      );
    }
    throw e;
  }

  return {
    added: response.added,
    failed: response.skipped_additions.filter((skip) => skip.status_code >= 400),
  };
};
